import classnames from "classnames";
import * as React from 'react';
import style from './SidebarMenu.scss';
import { PropsWithChildren } from "react";

type Props = PropsWithChildren<{
  scrollbarClassname: string
}>;
type State = {};

export default class SidebarMenu extends React.PureComponent<Props, State> {
  wrapperRef = React.createRef<HTMLDivElement>();
  contentRef = React.createRef<HTMLDivElement>();
  contentInnerRef = React.createRef<HTMLDivElement>();

  _scrollRatio = 0;
  _lastContentHeight = 0;
  _destroyed = false;

  resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.updateScrollbarSize));

  componentDidMount() {
    this.resizeObserver.observe(this.contentInnerRef.current);
    window.addEventListener('resize', this.updateScrollbarSize);
    this.updateScrollbarSize();
  }

  componentWillUnmount() {
    this._destroyed = true;
    this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.updateScrollbarSize);
  }

  updateScrollbarSize = () => {
    if (this._destroyed) return;
    const wrapperEl = this.wrapperRef.current;
    const contentEl = this.contentRef.current;
    const scrollbarHeight = contentEl.clientHeight / contentEl.scrollHeight * 100;
    this._scrollRatio = contentEl.scrollHeight / contentEl.clientHeight;
    const isActive = scrollbarHeight < 100;
    wrapperEl.style.setProperty('--scrollbar-track-height', scrollbarHeight.toFixed(2) + '%');
    wrapperEl.style.setProperty('--scrollbar-display', isActive ? 'block' : 'none');
    wrapperEl.style.setProperty('--scrollbar-margin', isActive ? 'var(--scrollbar-width, 0)' : '0');
    this._lastContentHeight = this.contentInnerRef.current.scrollHeight;
  }

  updateScrollPosition = () => {
    const { scrollTop, clientHeight, scrollHeight } = this.contentRef.current;
    const scrollPercent = scrollTop / (scrollHeight - clientHeight);
    this.wrapperRef.current.style.setProperty('--scrollbar-position', scrollPercent.toFixed(2));
  }

  onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    requestAnimationFrame(this.updateScrollPosition);
  }

  onStartDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = document;
    let lastPageY = e.clientY;

    const drag = (e: MouseEvent) => {
      const delta = e.clientY - lastPageY;
      lastPageY = e.clientY;

      requestAnimationFrame(() => {
        this.contentRef.current.scrollTop += delta * this._scrollRatio;
      });
    }

    const stop = () => {
      d.removeEventListener('mousemove', drag);
      d.removeEventListener('pointerup', stop);
    }
    d.addEventListener('mousemove', drag);
    d.addEventListener('pointerup', stop);
  }

  render() {
    return (
      <div ref={this.wrapperRef} className={style.wrapper}>
        <div className={style.container}>
          <div ref={this.contentRef} className={style.content} onScroll={this.onScroll}>
            <div ref={this.contentInnerRef} className={style.contentInner}>
              {this.props.children}
            </div>
          </div>
        </div>
        <div className={classnames(style.scrollbar, this.props.scrollbarClassname)}>
          <div className={style.track} onMouseDown={this.onStartDrag} />
        </div>
      </div>
    );
  };
}
